import React, { useEffect, useRef } from 'react';

/**
 * AG-Grid 필터 클래스를 React 컴포넌트로 래핑
 * @param {Object} props
 * @param {Class} props.FilterClass - AG-Grid 필터 클래스 (예: MoneyRangeFilter)
 * @param {string} props.value - 필터 값 (예: "1000~5000")
 * @param {Function} props.onChange - 값 변경 핸들러
 * @param {Function} props.onClose - 닫기 핸들러
 */
function FilterWrapper({ FilterClass, value, onChange, onClose }) {
  const containerRef = useRef(null);
  const filterInstanceRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !FilterClass) return;

    // AG-Grid 필터 인스턴스 생성
    const filterInstance = new FilterClass();
    filterInstanceRef.current = filterInstance;

    // Mock params for AG-Grid filter
    const params = {
      filterChangedCallback: () => {
        // 필터 모델이 변경되면 onChange 호출
        const model = filterInstance.getModel();
        if (!model) {
          onChange('');
        } else if (model.min !== undefined && model.max !== undefined) {
          // MoneyRangeFilter, HouseholdRangeFilter, CountRangeFilter
          onChange(`${model.min}~${model.max}`);
        } else if (model.from !== undefined && model.to !== undefined) {
          if (model.unit === 'pyeong' || model.unit === 'm2') {
            // AreaRangeFilter — 값은 원래 단위 그대로 두고 단위를 뒤에 실어서 왕복 유지
            onChange(`${model.from}~${model.to}~${model.unit}`);
          } else {
            // BuiltYearFilter (연도 from/to)
            onChange(`${model.from}~${model.to}`);
          }
        } else if (model.structure !== undefined) {
          // HouseStructureFilter
          onChange(model.structure);
        } else if (model.selected && Array.isArray(model.selected)) {
          // GradeFilter, GubunFilter, MultiSelectListFilter
          onChange(model.selected.join(','));
        }
      },
      onClose: onClose,
      api: null,
      column: null,
      colDef: {},
    };

    // 필터 초기화
    filterInstance.init(params);

    // 초기 값 설정
    if (value) {
      // 범위 필터: "1000~5000" 형식
      if (value.includes('~')) {
        const parts = value.split('~');
        const minVal = parts[0] ? Number(parts[0]) : undefined;
        const maxVal = parts[1] ? Number(parts[1]) : undefined;

        // AreaRangeFilter: { from, to, unit } (값 뒤에 단위 실려옴 "30~40~pyeong")
        // BuiltYearFilter: { from, to }
        // MoneyRangeFilter, HouseholdRangeFilter 등: { min, max }
        const isArea = filterInstance.unit !== undefined;
        const hasFrom = filterInstance.from !== undefined;

        if (isArea) {
          const unit = (parts[2] === 'm2' || parts[2] === 'pyeong') ? parts[2] : undefined;
          const model = {};
          if (minVal !== undefined && Number.isFinite(minVal)) model.from = minVal;
          if (maxVal !== undefined && Number.isFinite(maxVal)) model.to = maxVal;
          if (unit) model.unit = unit;
          if (model.from !== undefined || model.to !== undefined) {
            filterInstance.setModel(model);
          }
        } else if (hasFrom) {
          // BuiltYearFilter
          const model = {};
          if (minVal !== undefined && Number.isFinite(minVal)) model.from = minVal;
          if (maxVal !== undefined && Number.isFinite(maxVal)) model.to = maxVal;
          if (Object.keys(model).length > 0) {
            filterInstance.setModel(model);
          }
        } else {
          // MoneyRangeFilter, HouseholdRangeFilter 등
          const model = {};
          if (minVal !== undefined && Number.isFinite(minVal)) model.min = minVal;
          if (maxVal !== undefined && Number.isFinite(maxVal)) model.max = maxVal;
          if (Object.keys(model).length > 0) {
            filterInstance.setModel(model);
          }
        }
      }
      // 선택형 필터(GradeFilter/GubunFilter/MultiSelectListFilter)는 { selected: [...] } 모델 사용.
      // 값이 단일(콤마 없음)이어도 HouseStructureFilter로 오인하지 않도록 필터 타입(isSelected 메서드 유무)으로 분기한다.
      // (예: 구분 1개만 선택 → "LEGEND" → 재오픈 시 선택 상태가 사라지던 버그 방지)
      else if (typeof filterInstance.isSelected === 'function') {
        const parts = value.split(',').map((v) => v.trim()).filter((v) => v !== '');
        if (parts.length > 0) {
          // GradeFilter는 숫자, GubunFilter/MultiSelect는 문자열
          const allNumeric = parts.every((v) => !isNaN(Number(v)) && Number.isFinite(Number(v)));
          const selected = allNumeric ? parts.map((v) => Number(v)) : parts;
          if (selected.length > 0) {
            filterInstance.setModel({ selected });
          }
        }
      }
      // HouseStructureFilter: "계32" 형식 (단일 문자열)
      else {
        filterInstance.setModel({ structure: value });
      }
    }

    // DOM에 추가
    const gui = filterInstance.getGui();
    if (gui && containerRef.current) {
      containerRef.current.appendChild(gui);
    }

    // Cleanup
    return () => {
      if (containerRef.current && gui) {
        containerRef.current.removeChild(gui);
      }
    };
  }, [FilterClass, value, onChange, onClose]);

  return <div ref={containerRef} />;
}

export default FilterWrapper;
